/**
 * i18n — the whole runtime, in vanilla TypeScript.
 *
 * The app is plain TS and Tailwind. A framework i18n runtime (vue-i18n, i18next) would be the
 * only framework in the bundle, shipped to every visitor to do what a dotted lookup and a
 * regex do here. So: no dependency, ~200 lines, and `Intl` for everything a table would
 * otherwise get wrong (plural categories, dates, numbers).
 *
 * Messages are merged in this order, later wins:
 *   1. `locales/<id>.json` — the committed baseline, the catalogue this repo owns.
 *   2. `GET /api/i18n/<id>` — the admin/backend layer (server error and email copy). It is an
 *      OVERRIDE, never the source: it does not contain the UI catalogue and must not be able
 *      to blank a key by being absent.
 *
 * `en_GB` is a static import, and that is the load-bearing decision in this file. `t()` is
 * synchronous and callable before `initI18n()` resolves, so the first paint renders English
 * rather than raw dotted keys while a chunk and an HTTP request are still in flight. Every
 * other locale is a lazy chunk (`import.meta.glob`, one chunk per file).
 *
 * A missing key falls back: active locale → `en_GB` → the key itself. Never an empty string —
 * an empty element is a hole a reviewer cannot see, a visible `nav.play` is a bug report.
 */
import { api } from '../../core/network/api';
import {
  DEFAULT_LOCALE,
  detectLocale,
  isLocaleId,
  isPluralNode,
  localeTag,
  type LocaleId,
} from './locales';
import enGB from './locales/en_GB.json';

/** Where a guest's pick is remembered. Copy is not a secret, so `localStorage` is fine. */
export const STORAGE_KEY = 'bl.locale';

export type Messages = Record<string, unknown>;

const catalogues = new Map<LocaleId, Messages>([[DEFAULT_LOCALE, enGB as Messages]]);
const overridesFetched = new Set<LocaleId>();

let active: LocaleId = DEFAULT_LOCALE;

/** Lazy baselines — one Vite chunk per locale file. `en_GB` is also here and never used. */
const baselines = import.meta.glob<{ default: Messages }>('./locales/*.json');

// ── Lookup ──────────────────────────────────────────────────────────────────────────────────

function walk(bundle: Messages | undefined, key: string): unknown {
  if (!bundle) return undefined;
  let node: unknown = bundle;
  for (const segment of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

/** `{name}` → `vars.name`. An unknown placeholder is left verbatim so it reads as a defect. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole);
}

function resolve(key: string): unknown {
  const hit = walk(catalogues.get(active), key);
  if (hit !== undefined) return hit;
  return active === DEFAULT_LOCALE ? undefined : walk(catalogues.get(DEFAULT_LOCALE), key);
}

/**
 * Translate. Synchronous, and safe from the first line of `main.ts`.
 * Falls back through `en_GB` to the key itself — a missing key never renders empty.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const value = resolve(key);
  return typeof value === 'string' ? interpolate(value, vars) : interpolate(key, vars);
}

/**
 * Pluralised translate. `key` addresses a node of CLDR categories
 * (`{ one, other }`, plus `few`/`many` where a language needs them) and `Intl.PluralRules`
 * picks the branch — there is no hand-rolled table here, because Polish alone has four forms
 * and every such table in the wild is subtly wrong.
 *
 * `{n}` and `{count}` are always available to the string.
 */
export function plural(key: string, n: number, vars?: Record<string, string | number>): string {
  const node = resolve(key);
  const all = { n, count: n, ...vars };
  if (!isPluralNode(node)) return t(key, all);

  let category = 'other';
  try {
    category = new Intl.PluralRules(localeTag(active)).select(n);
  } catch {
    category = n === 1 ? 'one' : 'other';
  }
  const form = node[category] ?? node.other ?? node.many ?? node.one ?? Object.values(node)[0];
  return typeof form === 'string' ? interpolate(form, all) : interpolate(key, all);
}

// ── Formatting ──────────────────────────────────────────────────────────────────────────────

/** Numbers in the active locale — scores, credits, percentages. */
export function formatNumber(value: number, opts: Intl.NumberFormatOptions = {}): string {
  try {
    return new Intl.NumberFormat(localeTag(active), opts).format(value);
  } catch {
    return String(value);
  }
}

/** Dates in the active locale. An unparseable value renders an em dash, never `Invalid Date`. */
export function formatDate(
  value: string | number | Date,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(localeTag(active), opts).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

// ── Subscribers ─────────────────────────────────────────────────────────────────────────────

const listeners = new Set<(id: LocaleId) => void>();

/** Register a repaint callback — nothing here is reactive, screens redraw by hand. */
export function subscribe(fn: (id: LocaleId) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ── Loading ─────────────────────────────────────────────────────────────────────────────────

function deepMerge(base: Messages, over: Messages): Messages {
  const out: Messages = { ...base };
  for (const [k, v] of Object.entries(over)) {
    const prev = out[k];
    const bothObjects = v !== null && typeof v === 'object' && !Array.isArray(v)
      && prev !== null && typeof prev === 'object' && !Array.isArray(prev);
    out[k] = bothObjects
      ? deepMerge(prev as Messages, v as Messages)
      : v;
  }
  return out;
}

/** The backend override layer. Never throws — a dead endpoint means no overrides, not no UI. */
async function fetchOverrides(id: LocaleId): Promise<Messages> {
  try {
    const res = await api.get<{ locale: string; messages: Messages }>(`/i18n/${id}`);
    return res?.messages ?? {};
  } catch {
    return {};
  }
}

/** Ensure a locale's catalogue is in memory. Idempotent; each locale is fetched once. */
export async function loadLocale(id: LocaleId): Promise<void> {
  let messages = catalogues.get(id);

  if (!messages) {
    const loader = baselines[`./locales/${id}.json`];
    try {
      messages = loader ? (await loader()).default : {};
    } catch {
      // Chunk missing (bad deploy, offline) — an empty baseline still falls back per key.
      messages = {};
    }
  }

  if (!overridesFetched.has(id)) {
    overridesFetched.add(id);
    messages = deepMerge(messages, await fetchOverrides(id));
  }

  catalogues.set(id, messages);
}

/**
 * Switch tongues: load the catalogue, flip the active locale, update `<html lang>` (screen
 * readers, hyphenation and `:lang()` all read it), remember the pick, repaint the subscribers.
 */
export async function setLocale(id: LocaleId): Promise<void> {
  const target = isLocaleId(id) ? id : DEFAULT_LOCALE;
  await loadLocale(target);
  active = target;

  if (typeof document !== 'undefined') document.documentElement.lang = localeTag(target);
  try {
    localStorage.setItem(STORAGE_KEY, target);
  } catch {
    // Private mode or storage disabled — the pick just will not survive a reload.
  }
  for (const fn of [...listeners]) fn(target);
}

export function currentLocale(): LocaleId {
  return active;
}

/** The remembered pick, if it is still a locale this build enables. */
export function storedLocale(): LocaleId | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocaleId(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Boot. Honours a remembered pick, else the browser's language list, else `en_GB`. Awaiting it
 * before the first paint means a returning German visitor never sees a flash of English —
 * but not awaiting it is also safe, which is the point of the eager fallback.
 */
export async function initI18n(): Promise<void> {
  const langs = typeof navigator !== 'undefined'
    ? (navigator.languages ?? (navigator.language ? [navigator.language] : []))
    : [];
  await setLocale(detectLocale(langs, storedLocale()));
}

/**
 * The catalogue for a locale, flattened to dotted keys — plural nodes counted as one key.
 * Exported for the parity test: a key present in `en_GB` and missing in Polish is the exact
 * failure this module exists to prevent, and it is only catchable by comparing key sets.
 */
export function flattenKeys(bundle: Messages, prefix = ''): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(bundle)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v) && !isPluralNode(v)) {
      out.push(...flattenKeys(v as Messages, path));
    } else {
      out.push(path);
    }
  }
  return out.sort();
}

/** Test/diagnostic access to what is currently loaded. Not for render paths — use `t()`. */
export function loadedCatalogue(id: LocaleId): Messages | undefined {
  return catalogues.get(id);
}

export {
  LOCALES,
  LOCALE_IDS,
  DEFAULT_LOCALE,
  hasLocaleChoice,
  isLocaleId,
  isPluralNode,
  localeLabel,
  localeMeta,
  localeTag,
  detectLocale,
} from './locales';
export type { LocaleId, LocaleMeta } from './locales';
